if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      pendingVariantMediaBehavior = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.dataset.variantRequestPending = 'false';

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      setVariantRequestPending(isPending) {
        this.dataset.variantRequestPending = isPending ? 'true' : 'false';
        if (!isPending) {
          this.dispatchEvent(new CustomEvent('product-info:request-complete', { bubbles: true }));
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        const changedOptionName = this.getChangedOptionName(event?.target);
        this.pendingVariantMediaBehavior = this.getVariantMediaBehavior(changedOptionName);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
        this.toggleInventoryLoading(true);
      }

      toggleInventoryLoading(isLoading) {
        const inventories = this.querySelectorAll(
          `#Inventory-${this.dataset.section}, #Inventory-${this.dataset.section}-price`
        );
        if (!inventories.length) return;
        inventories.forEach((inventory) => {
          inventory.classList.toggle('is-loading', isLoading);
          inventory.toggleAttribute('aria-busy', isLoading);
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.activeVariantRequestId = requestId;
        this.setVariantRequestPending(true);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
              this.toggleInventoryLoading(false);
            }
          })
          .finally(() => {
            if (this.activeVariantRequestId !== requestId) return;
            this.activeVariantRequestId = null;
            this.setVariantRequestPending(false);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);

        if (shouldFetchFullPage) {
          requestUrl.searchParams.delete('section_id');
        } else {
          requestUrl.searchParams.set('section_id', this.sectionId);
        }

        const normalizedOptionValues = Array.isArray(optionValues) ? optionValues.filter(Boolean) : [];
        if (normalizedOptionValues.length) {
          requestUrl.searchParams.set('option_values', normalizedOptionValues.join(','));
          // `option_values` should be the source of truth for variant resolution.
          requestUrl.searchParams.delete('variant');
        } else {
          requestUrl.searchParams.delete('option_values');
        }

        return requestUrl.toString();
      }

      getChangedOptionName(changedInput) {
        const input = changedInput?.tagName === 'OPTION' ? changedInput.closest('select') : changedInput;
        if (!input) return '';

        const rawName = input.getAttribute('name') || '';
        if (!rawName) return '';

        const optionSelectMatch = rawName.match(/^options\[(.+)\]$/);
        if (optionSelectMatch && optionSelectMatch[1]) return optionSelectMatch[1].trim();

        const nameParts = rawName.split('-');
        if (nameParts.length > 1) return nameParts.slice(0, -1).join('-').trim();
        return rawName.trim();
      }

      getVariantMediaBehavior(changedOptionName = '') {
        const variantSelectors = this.variantSelectors;
        const configuredNamesRaw = variantSelectors?.dataset.mediaUpdateOptionNames || '';
        const configuredNames = configuredNamesRaw
          .split(',')
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);
        const normalizedChangedName = String(changedOptionName || '')
          .trim()
          .toLowerCase();

        const shouldUpdateMedia =
          configuredNames.length === 0 ||
          (normalizedChangedName !== '' && configuredNames.includes(normalizedChangedName));
        const shouldScrollOnMediaUpdate = variantSelectors?.dataset.mediaUpdateScrollToMedia !== 'false';

        return {
          changedOptionName,
          shouldUpdateMedia,
          shouldScrollOnMediaUpdate,
        };
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateSizetableModalContent(html) {
        const sourceModals = html.querySelectorAll(
          'modal-dialog[id^="PopupModal-"][data-sizetable-variant-image-sync="true"]'
        );
        if (!sourceModals.length) return;

        sourceModals.forEach((sourceModal) => {
          const targetModal =
            document.querySelector(
              `modal-dialog[id="${sourceModal.id}"][data-section="${this.dataset.section}"]`
            ) || document.getElementById(sourceModal.id);
          if (!targetModal) return;

          const hydrateIfOpen = () => {
            if (!targetModal.hasAttribute('open')) return;
            if (typeof targetModal.hydrateDeferredContent === 'function') {
              targetModal.hydrateDeferredContent();
            }
          };

          const sourceExtra = sourceModal.querySelector('.sizetable-popup-extra');
          const targetExtra = targetModal.querySelector('.sizetable-popup-extra');

          if (sourceExtra && targetExtra) {
            targetExtra.replaceWith(sourceExtra.cloneNode(true));
            hydrateIfOpen();
            return;
          }

          if (sourceExtra && !targetExtra) {
            const targetBody = targetModal.querySelector('.price-info-modal__body');
            targetBody?.append(sourceExtra.cloneNode(true));
            hydrateIfOpen();
            return;
          }

          if (!sourceExtra && targetExtra) {
            targetExtra.remove();
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const mediaBehavior = this.pendingVariantMediaBehavior || this.getVariantMediaBehavior('');
          this.pendingVariantMediaBehavior = null;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (mediaBehavior.shouldUpdateMedia) {
            this.updateMedia(html, variant?.featured_media?.id, {
              shouldScrollOnMediaUpdate: mediaBehavior.shouldScrollOnMediaUpdate,
            });
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.updateSizetableModalContent(html);
          this.toggleInventoryLoading(false);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
              changedOptionName: mediaBehavior.changedOptionName,
              mediaBehavior,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.toggleInventoryLoading(false);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, options = {}) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const skipPrune = this.dataset.skipVariantMediaPrune === 'true';

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          if (!skipPrune) {
            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const mediaGalleryElement = this.querySelector('media-gallery');
        const shouldPrependActiveMedia = !mediaGalleryElement?.variantMediaData;

        // set featured media as active in the media gallery
        mediaGalleryElement?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          shouldPrependActiveMedia,
          {
            fromVariantChange: true,
            shouldScrollToMedia: options.shouldScrollOnMediaUpdate !== false,
          }
        );
        this.refreshQuickAddMobileMediaSkeletons(mediaGalleryElement);

        // update media modal
        const modalMap = this.productModal?.querySelector('[data-variant-media-map]');
        const newModalMap = html.querySelector(`product-modal [data-variant-media-map]`);
        if (modalMap && newModalMap) {
          modalMap.textContent = newModalMap.textContent;
        }
        const nextVariantId =
          html.querySelector('input[name="id"]')?.value || this.querySelector('input[name="id"]')?.value || null;
        if (this.productModal && nextVariantId) {
          this.productModal.dataset.currentVariantId = String(nextVariantId);
        }

        const modalContentIsDeferred =
          this.productModal?.dataset.productModalDeferred === 'true' &&
          this.productModal?.dataset.productModalLoaded !== 'true';
        if (!modalContentIsDeferred) {
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      refreshQuickAddMobileMediaSkeletons(mediaGalleryElement = this.querySelector('media-gallery')) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (!this.closest('quick-add-modal')) return;
        if (!mediaGalleryElement?.initMobileMediaSkeletons) return;

        const skeletonSlides = Array.from(
          mediaGalleryElement.querySelectorAll('.product__media-item--mobile-skeleton')
        );
        if (!skeletonSlides.length) return;

        skeletonSlides.forEach((slide, index) => {
          delete slide.dataset.mobileSkeletonBound;

          const image = slide.querySelector('.product__media img');
          if (image && index < 2) {
            image.setAttribute('loading', 'eager');
            image.setAttribute('decoding', 'async');
            image.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
          }
        });

        mediaGalleryElement.initMobileMediaSkeletons();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const loadingSpinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        loadingSpinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => loadingSpinner?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }

        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
